// Import 3rd-party dependencies
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

// Import project-specific dependencies
import { ApiService } from '@app/services/api.service';
import { environment } from '@app/environments/environment';
import { IResourceLink } from '@app/services/app-tools/app-tools.service';

export interface IFavoriteResourceLink extends IResourceLink {
  customLink: boolean;
  dateTimeFavorited: Date;
}

export interface IFavoriteLink {
  links: IFavoriteResourceLink[];
}

@Injectable({
  providedIn: 'root'
})
export class FavoriteService extends ApiService {
  private PROFILE_API_URL = environment.DEPS.PROFILE_API.URL;
  private favorites: IFavoriteResourceLink[] = [];
  private favoritesSubject = new BehaviorSubject<IFavoriteResourceLink[]>(this.favorites);
  readonly favorites$ = this.favoritesSubject.asObservable();

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  private resourceLinks$ = this.get<IFavoriteLink>(`${this.PROFILE_API_URL}/me/resourcelinks`).pipe(
    map((res) => res.links)
  );

  addFavorite(favorites: IFavoriteResourceLink[]): void {
    this.favorites.push(...favorites);
    this.favoritesSubject.next(this.favorites);
    this.put(`${this.PROFILE_API_URL}/me/resourcelinks`, { links: this.favorites }).subscribe();
  }

  updateFavorite(oldFavorite: IFavoriteResourceLink, newFavorite: IFavoriteResourceLink): void {
    const idx = this.favorites.findIndex((fav) => fav.name === oldFavorite.name);
    if (idx > -1) {
      this.favorites[idx] = newFavorite;
      this.favoritesSubject.next(this.favorites);
      this.put(`${this.PROFILE_API_URL}/me/resourcelinks`, { links: this.favorites }).subscribe();
    }
  }

  removeFavorite(favorite: IFavoriteResourceLink): void {
    const idx = this.favorites.findIndex((item) => item.name === favorite.name);
    if (idx > -1) {
      this.favorites.splice(idx, 1);
      this.favoritesSubject.next(this.favorites);
      this.put(`${this.PROFILE_API_URL}/me/resourcelinks`, { links: this.favorites }).subscribe();
    }
  }

  getFavoriteList(): void {
    this.resourceLinks$.subscribe((favorites) => {
      this.favorites = favorites;
      this.favoritesSubject.next(this.favorites);
    });
  }
}
